import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, LayoutGrid } from "lucide-react";
import AppBar from "./app-bar";
import AppMenu from "./app-menu";
import AppHub from "./app-hub";
import { AppNavContext, bindNav } from "./app-nav";
import { APP_BY_TYPE } from "./app-catalog";
import { RENDERERS } from "./sections";
import type { SectionProps } from "./context";
import { t } from "@/lib/i18n";

type Props = { sections: Record<string, SectionProps> };

/** Рабочее пространство: хаб со всеми разделами + показ одного выбранного раздела на весь экран. */
export default function AppShell({ sections }: Props) {
  const [current, setCurrent] = useState<string | null>(null);

  const nav = useMemo(
    () => ({
      open: (type: string) => setCurrent(type),
      home: () => setCurrent(null),
      current,
    }),
    [current],
  );

  useEffect(() => {
    bindNav(setCurrent);
    return () => bindNav(null);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [current]);

  const item = current ? APP_BY_TYPE[current] : undefined;
  const Section = current ? RENDERERS[current] : undefined;
  const props = current ? sections[current] : undefined;
  const ready = Boolean(item && Section && props);

  return (
    <AppNavContext.Provider value={nav}>
      <AppBar />
      {ready && item && Section && props ? (
        <main id="main" className="flex flex-col">
          <div className="border-b border-border bg-background-100/70">
            <div className="mx-auto flex w-full max-w-[1440px] flex-wrap items-center gap-2 px-4 py-2.5 md:px-6">
              <button
                type="button"
                onClick={nav.home}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn px-2 py-1.5 text-[13px] font-semibold text-muted-foreground transition hover:bg-card hover:text-foreground"
              >
                <ChevronLeft className="size-4 flex-none" aria-hidden />
                {t("Все разделы")}
              </button>
              <span className="hidden h-4 w-px bg-border sm:block" aria-hidden />
              <span className="flex min-w-0 items-center gap-2 text-[13px] font-semibold text-foreground">
                <item.icon className="size-4 flex-none text-primary" aria-hidden />
                <span className="truncate">{item.label}</span>
                <span className="hidden truncate text-xs font-normal text-muted-foreground sm:inline">— {item.hint}</span>
              </span>
              <button
                type="button"
                onClick={nav.home}
                className="ms-auto inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn border border-border bg-card px-2.5 py-1.5 text-xs font-semibold text-muted-foreground transition hover:border-primary/60 hover:text-primary"
              >
                <LayoutGrid className="size-3.5 flex-none" aria-hidden />
                {t("Все разделы")}
              </button>
            </div>
          </div>
          <Section {...props} />
        </main>
      ) : (
        <AppHub />
      )}
      <AppMenu />
    </AppNavContext.Provider>
  );
}